import type { Market } from '@//config/domain-locale-map';
import { getRangeFacetUnit } from '@//utils/vehicles-search/getRangeFacetUnit';
import type {
  EnrichedElasticSearchResponseFacet,
  RangeFacetKeys,
} from '@//utils/vehicles-search/useEnrichedFacets';

const parseNumber = (s: string) => {
  // Remove thousand separators (space, comma), "<", and "+" but keep decimal point
  // Then parse as float and convert to integer
  const cleaned = s.replace(/[\s,<+]/g, '');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : Math.floor(n);
};

/**
 * Formats a number with thousand separators according to locale
 */
const formatNumber = (value: number, locale: string): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const getLocationActiveFacetLabel = (searchParams: URLSearchParams) => {
  const locationName = decodeURIComponent(searchParams.get('position.name')?.split(',')?.[0] || '');
  const locationDistance = searchParams.get('position.location');
  return `${locationName} +${locationDistance} km`; // Geographic distance always in km
};

interface GetRangeActiveFacetLabel {
  facet: EnrichedElasticSearchResponseFacet;
  searchParams: URLSearchParams;
  locale: string;
  market: Market;
  currencySymbol: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: any) => string;
}

export const getRangeActiveFacetLabel = ({
  facet,
  searchParams,
  locale,
  market,
  currencySymbol,
  t,
}: GetRangeActiveFacetLabel) => {
  const minStr = searchParams.get(`${facet.id}.min`);
  const maxStr = searchParams.get(`${facet.id}.max`);

  // Parse all filter values once (reuse for both global bounds and max check)
  const values = facet.filters
    .map((flt) => parseNumber(flt.displayName))
    .filter((n) => n !== null) as number[];
  values.sort((a, b) => a - b);

  const globalMin = values[0];
  const globalMax = values[values.length - 1];
  const min = minStr ? (parseNumber(minStr) ?? globalMin) : globalMin;
  const max = maxStr ? (parseNumber(maxStr) ?? globalMax) : globalMax;
  const key = facet.id as RangeFacetKeys;

  // Check if the maximum filter value's displayName ends with "+"
  const maxFilter = facet.filters.find((flt) => parseNumber(flt.displayName) === globalMax);
  const hasOpenEndedMax = maxFilter?.displayName.endsWith('+') ?? false;

  // Check if the minimum filter value's displayName starts with "<"
  const minFilter = facet.filters.find((flt) => parseNumber(flt.displayName) === globalMin);
  const hasOpenEndedMin = minFilter?.displayName.startsWith('<') ?? false;

  // Get unit for min value
  const minUnit = getRangeFacetUnit({
    facetKey: key,
    market,
    currencySymbol,
    value: min,
    t,
  });

  // Get unit for max value (important for properties.seats with plural)
  const maxUnit = getRangeFacetUnit({
    facetKey: key,
    market,
    currencySymbol,
    value: max,
    t,
  });

  // Determine which facets should use thousand separators
  const useThousandSeparator =
    key === 'price' ||
    key === 'mileage' ||
    key === 'odometerValueAsKm' ||
    key === 'enhancedProperties.electricalRangeWLTP.value' ||
    key === 'enginePower';

  // Format values with or without thousand separators
  const formattedMin = useThousandSeparator ? formatNumber(min, locale) : min.toString();
  const formattedMax = useThousandSeparator ? formatNumber(max, locale) : max.toString();

  // Prepend "<" to min value if it equals the global minimum AND the original displayName had a "<"
  const minDisplay = min === globalMin && hasOpenEndedMin ? `<${formattedMin}` : formattedMin;

  // Append "+" to max value if it equals the global maximum AND the original displayName had a "+"
  const maxDisplay = max === globalMax && hasOpenEndedMax ? `${formattedMax}+` : formattedMax;

  // If units differ (e.g., "1 säte" to "5 säten"), show unit for each value
  if (minUnit !== maxUnit) {
    return `${minDisplay} ${minUnit}–${maxDisplay} ${maxUnit}`;
  }

  // Otherwise, show unit once at the end
  return minUnit ? `${minDisplay}–${maxDisplay} ${minUnit}` : `${minDisplay}–${maxDisplay}`;
};
